import React, { useEffect, useState } from 'react';
import { sessionsApi } from '../../api/sessions';
import NewSessionDialog from '../Session/NewSessionDialog';
import './ChatPaneFrame.css';

export const CHAT_FOLD_KEY = 'brainstormy.chatPaneFolded';

/** Outline and Scene writing start closed until the writer chooses. */
const CHAT_FOLD_CLOSED_ROOMS = new Set(['outline', 'write']);

const SUBJECT_FOLD_ROOM = {
  Outline: 'outline',
  Write: 'write',
  Notes: 'notes',
  Docs: 'docs',
  Idea: 'idea',
};

/** Room for the fold flag. A room key wins; a known title is the fallback. */
export function chatFoldRoom(roomKey, subject) {
  const key = String(roomKey || '').trim().toLowerCase();
  if (key) return key;
  return SUBJECT_FOLD_ROOM[String(subject || '').trim()] || '';
}

export function chatFoldStorageKey(room) {
  return room ? `${CHAT_FOLD_KEY}.${room}` : CHAT_FOLD_KEY;
}

export function readChatFolded(roomKey, subject) {
  const room = chatFoldRoom(roomKey, subject);
  try {
    const stored = localStorage.getItem(chatFoldStorageKey(room));
    if (stored === '1') return true;
    if (stored === '0') return false;
  } catch {
    return CHAT_FOLD_CLOSED_ROOMS.has(room);
  }
  return CHAT_FOLD_CLOSED_ROOMS.has(room);
}

export function writeChatFolded(folded, roomKey, subject) {
  const room = chatFoldRoom(roomKey, subject);
  try {
    localStorage.setItem(chatFoldStorageKey(room), folded ? '1' : '0');
  } catch {
    /* the pane still folds for this view */
  }
}

export const CHAT_FOCUS_EVENT = 'brainstormy-chat-focus';
export const CHAT_UNFOLD_EVENT = 'brainstormy-chat-unfold';

/** Open a hidden room chat without changing its subject. */
export function publishChatUnfold() {
  writeChatFolded(false);
  window.dispatchEvent(new Event(CHAT_UNFOLD_EVENT));
}

export const CHAT_FOCUS_SECTION_EVENT = 'brainstormy-chat-focus-section';
export const CHAT_SCENE_REMOVED_EVENT = 'brainstormy-chat-scene-removed';
export const CHAT_SUBJECT_EVENT = 'brainstormy-chat-subject';

/** Update the pane title without opening a folded chat. */
export function publishChatSubject(subject) {
  window.dispatchEvent(new CustomEvent(CHAT_SUBJECT_EVENT, { detail: String(subject || '') }));
}

/** "Scene 1.1" when the outline numbered it, otherwise the Write room. */
export function writeRoomChatSubject(scene) {
  const number = String(scene?.number || '').trim();
  return number ? `Scene ${number}` : 'Write';
}

/** Scene id for the next chat send. An empty id clears a previous scene. */
export function publishChatSection(sectionId) {
  window.dispatchEvent(new CustomEvent(CHAT_FOCUS_SECTION_EVENT, {
    detail: sectionId ? String(sectionId) : '',
  }));
}

/** The focused scene is gone. The header drops that name when the id matches. */
export function publishChatSceneRemoved(sceneId) {
  window.dispatchEvent(new CustomEvent(CHAT_SCENE_REMOVED_EVENT, {
    detail: sceneId ? String(sceneId) : '',
  }));
}

/** Point the shared pane at one scene and open it if it was hidden.
 * Pass a section id when the writer means one scene. Omit it to leave
 * the current scene id alone. Pass an empty string to clear it.
 */
export function publishChatFocus(subject, sectionId) {
  writeChatFolded(false);
  window.dispatchEvent(new CustomEvent(CHAT_FOCUS_EVENT, { detail: String(subject || '') }));
  if (sectionId !== undefined) publishChatSection(sectionId);
}

/**
 * Room lists can omit the chat that is open. Keep that row, in the
 * same style as the others, so Past chats is empty only when none exist.
 */
/**
 * Write Past chats shows this scene plus the legacy book thread.
 * Another scene's thread stays off this list. Other rooms are unchanged.
 */
export function writePastChatRows(rows, roomKey, sceneId) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  if (roomKey !== 'write' || !sceneId) return list;
  return list.filter((row) => {
    const bound = row?.write_scene_id || null;
    return !bound || String(bound) === String(sceneId);
  });
}

export function sessionsWithOpenChat(rows, activeSessionId, activeSessionName) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  if (!activeSessionId) return list;
  if (list.some((row) => String(row?.id) === String(activeSessionId))) return list;
  return [
    { id: activeSessionId, name: activeSessionName || 'Untitled chat' },
    ...list,
  ];
}

/**
 * Shared chat header: "Chat · X", Past chats, and Hide chat.
 * Fold is remembered per room on this browser. Outline and Write
 * start closed until the writer opens or closes chat there.
 * New chat lives in Past chats.
 */
export default function ChatPaneFrame({
  subject = 'this session',
  storyId = null,
  projectId = null,
  storyName = '',
  activeSessionId = null,
  activeSessionName = '',
  onSelectSession,
  foldable = false,
  roomKey = null,
  writeSceneId = null,
  onNewRoomChat = null,
  headerExtra = null,
  children,
}) {
  const [folded, setFolded] = useState(() => (
    foldable ? readChatFolded(roomKey, subject) : false
  ));
  const [menuOpen, setMenuOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    if (!foldable) {
      setFolded(false);
      return undefined;
    }
    setFolded(readChatFolded(roomKey, subject));
    return undefined;
  }, [foldable, roomKey, subject]);

  useEffect(() => {
    if (!foldable) return undefined;
    const onOpen = () => {
      writeChatFolded(false, roomKey, subject);
      setFolded(false);
    };
    window.addEventListener(CHAT_FOCUS_EVENT, onOpen);
    window.addEventListener(CHAT_UNFOLD_EVENT, onOpen);
    return () => {
      window.removeEventListener(CHAT_FOCUS_EVENT, onOpen);
      window.removeEventListener(CHAT_UNFOLD_EVENT, onOpen);
    };
  }, [foldable, roomKey, subject]);

  useEffect(() => {
    if (!menuOpen || !storyId) return undefined;
    let cancelled = false;
    const listOptions = roomKey
      ? {
        room: roomKey,
        ...(roomKey === 'write' && writeSceneId ? { scene: writeSceneId } : {}),
      }
      : undefined;
    sessionsApi.list(storyId, listOptions).then((data) => {
      if (cancelled) return;
      const rows = writePastChatRows(data, roomKey, writeSceneId);
      setSessions(sessionsWithOpenChat(rows, activeSessionId, activeSessionName));
    }).catch(() => {
      if (cancelled) return;
      setSessions(sessionsWithOpenChat([], activeSessionId, activeSessionName));
    });
    return () => { cancelled = true; };
  }, [menuOpen, storyId, roomKey, writeSceneId, activeSessionId, activeSessionName]);

  const title = `Chat · ${subject}`;

  const hide = () => {
    writeChatFolded(true, roomKey, subject);
    setFolded(true);
    setMenuOpen(false);
  };

  const show = () => {
    writeChatFolded(false, roomKey, subject);
    setFolded(false);
  };

  if (foldable && folded) {
    return (
      <button
        type="button"
        className="chat-pane__strip"
        aria-label="Show chat"
        onClick={show}
      >
        Chat
      </button>
    );
  }

  return (
    <div className="chat-pane">
      <div className="chat-pane__header">
        <p className="chat-pane__title">{title}</p>
        <div className="chat-pane__header-tools">
          <button
            type="button"
            className="chat-pane__control"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            Past chats
          </button>
          {foldable && (
            <button type="button" className="chat-pane__control" onClick={hide}>
              Hide chat
            </button>
          )}
          {headerExtra}
        </div>
      </div>
      {menuOpen && (
        <div className="chat-pane__menu" role="menu" aria-label="Past chats">
          <button
            type="button"
            role="menuitem"
            className="chat-pane__menu-item"
            disabled={!storyId}
            onClick={() => {
              setMenuOpen(false);
              if (onNewRoomChat) {
                onNewRoomChat();
                return;
              }
              setShowNew(true);
            }}
          >
            New chat
          </button>
          {sessions.map((session) => (
            <button
              key={session.id}
              type="button"
              role="menuitem"
              className="chat-pane__menu-item"
              aria-current={session.id === activeSessionId ? 'page' : undefined}
              onClick={() => {
                setMenuOpen(false);
                onSelectSession?.(session.id, session);
              }}
            >
              {session.name || 'Untitled chat'}
            </button>
          ))}
          {sessions.length === 0 && (
            <p className="chat-pane__menu-empty">No past chats yet.</p>
          )}
        </div>
      )}
      <div className="chat-pane__body">{children}</div>
      {showNew && storyId && (
        <NewSessionDialog
          storyId={storyId}
          projectId={projectId}
          storyName={storyName}
          seriesAltitude={false}
          onClose={() => setShowNew(false)}
          onSessionCreated={(session) => {
            setShowNew(false);
            if (session?.id) onSelectSession?.(session.id, session);
          }}
        />
      )}
    </div>
  );
}
